'use client'

import { AnimatePresence, m, useReducedMotion } from 'framer-motion'
import { Activity, Target } from 'lucide-react'
import { type KeyboardEvent as ReactKeyboardEvent, useState } from 'react'

export interface AudienceItem {
  id: string
  number: string
  title: string
  subtitle: string
  thesis: string
  description?: string
  color: string
  stats: string
  statsLabel: string
  pains: string[]
  solutions: string[]
}

type AudienceAccordionProps = {
  items: AudienceItem[]
  leftTitle?: string
  rightTitle?: string
}

export default function AudienceAccordion({
  items,
  leftTitle = 'Проблемы',
  rightTitle = 'Решение',
}: AudienceAccordionProps) {
  const [active, setActive] = useState<string>(() => items?.[0]?.id || '')
  const reduceMotion = useReducedMotion()

  if (!items || items.length === 0) return null

  const cardTransition = reduceMotion
    ? { duration: 0 }
    : ({ type: 'spring', stiffness: 240, damping: 34, mass: 0.85 } as const)

  function focusCardByIndex(index: number, doc: Document) {
    const el = doc.getElementById(`audience-card-${index}`)
    if (el instanceof HTMLElement) {
      el.focus()
    }
  }

  function handleCardKeyDown(
    event: ReactKeyboardEvent<HTMLElement>,
    index: number,
    itemId: string,
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setActive(itemId)
      return
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault()
      const nextIndex = (index + 1) % items.length
      setActive(items[nextIndex].id)
      focusCardByIndex(nextIndex, event.currentTarget.ownerDocument)
      return
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault()
      const prevIndex = (index - 1 + items.length) % items.length
      setActive(items[prevIndex].id)
      focusCardByIndex(prevIndex, event.currentTarget.ownerDocument)
      return
    }

    if (event.key === 'Home') {
      event.preventDefault()
      setActive(items[0].id)
      focusCardByIndex(0, event.currentTarget.ownerDocument)
      return
    }

    if (event.key === 'End') {
      event.preventDefault()
      const lastIndex = items.length - 1
      setActive(items[lastIndex].id)
      focusCardByIndex(lastIndex, event.currentTarget.ownerDocument)
    }
  }

  return (
    <section className="w-full py-8">
      <div className="flex w-full flex-col gap-4 sm:gap-6 lg:flex-row">
        {items.map((item, index) => {
          const isActive = active === item.id
          const cardId = `audience-card-${index}`
          const panelId = `audience-panel-${index}`
          const headingId = `audience-heading-${index}`

          return (
            <m.div
              key={item.id}
              layout
              initial={false}
              // On mobile we stack cards (flex-col). Using flex-basis:0 (via flex-1) can collapse height
              // in an auto-sized column flex container. Keep basis auto on mobile; only "flexy" on lg+.
              animate={{ flexGrow: isActive ? 4 : 0.65 }}
              transition={cardTransition}
              onMouseEnter={() => setActive(item.id)}
              className={[
                'relative w-full overflow-hidden rounded-[32px] border border-white/5 shadow-2xl ring-1 ring-white/5 lg:flex-1 lg:basis-0',
                'cursor-pointer bg-[#0b101b] hover:bg-[#111a2f]',
                isActive
                  ? 'bg-[#0c1220] lg:cursor-default'
                  : 'border-white/10 ring-white/10 hover:border-white/20 hover:ring-white/20',
                'will-change-transform',
              ].join(' ')}
            >
              <button
                id={cardId}
                type="button"
                aria-expanded={isActive}
                aria-controls={panelId}
                aria-labelledby={headingId}
                onClick={() => setActive(item.id)}
                onFocus={() => setActive(item.id)}
                onKeyDown={(event) => handleCardKeyDown(event, index, item.id)}
                className="absolute inset-0 z-20 rounded-[32px] focus-visible:ring-2 focus-visible:ring-blue-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b101b]"
              >
                <span className="sr-only">{item.title}</span>
              </button>

              <m.div
                className={`absolute inset-0 bg-gradient-to-br ${item.color}`}
                initial={false}
                animate={{ opacity: isActive ? 0.03 : 0 }}
                transition={
                  reduceMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
                }
              />

              <m.div
                className="pointer-events-none absolute -right-6 -bottom-10 text-[8rem] font-bold text-white/[0.02] select-none sm:-right-10 sm:text-[11rem] lg:-bottom-14 lg:text-[10rem] xl:-bottom-20 xl:text-[13rem]"
                initial={false}
                animate={{ x: isActive ? 0 : 220, opacity: isActive ? 1 : 0 }}
                transition={
                  reduceMotion ? { duration: 0 } : { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
                }
              >
                {item.number}
              </m.div>

              <div
                id={panelId}
                role="region"
                aria-labelledby={headingId}
                className="relative flex h-full min-h-0 w-full flex-col p-5 sm:p-8 lg:p-8 xl:p-12"
              >
                <span id={headingId} className="sr-only">
                  {item.title}
                </span>
                <AnimatePresence initial={false} mode="popLayout">
                  {isActive ? (
                    <m.div
                      key="active"
                      layout="position"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={
                        reduceMotion
                          ? { duration: 0 }
                          : { duration: 0.35, ease: [0.22, 1, 0.36, 1] }
                      }
                      className="flex min-h-0 flex-1 flex-col"
                    >
                      <div className="mb-6 flex items-start justify-between gap-6">
                        <div>
                          <h3 className="home-card-title-premium mb-3 text-balance text-white">
                            {item.title}
                          </h3>
                          <span
                            className={`home-caption bg-gradient-to-r font-bold tracking-[0.2em] uppercase ${item.color} bg-clip-text whitespace-nowrap text-transparent opacity-90`}
                          >
                            {item.subtitle}
                          </span>
                        </div>
                      </div>

                      <div className="min-h-0 flex-1">
                        <p className="home-lead mb-8 max-w-3xl leading-relaxed text-gray-200">
                          {item.thesis}
                        </p>

                        <div className="relative z-10 grid grid-cols-1 gap-x-10 gap-y-8 border-t border-white/5 pt-6 md:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
                          <div>
                            <h4 className="home-meta mb-6 flex items-center gap-2 text-gray-500 uppercase">
                              <Activity className="h-4 w-4" /> {leftTitle}
                            </h4>
                            <ul className="space-y-4">
                              {(item.pains || []).map((pain, i) => (
                                <li
                                  key={i}
                                  className="home-subnote group/item flex items-start gap-4 text-gray-400 transition-colors hover:text-gray-200"
                                >
                                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/50 transition-colors group-hover/item:bg-red-400" />
                                  {pain}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="home-meta mb-6 flex items-center gap-2 text-gray-500 uppercase">
                              <Target className="h-4 w-4" /> {rightTitle}
                            </h4>
                            <ul className="space-y-4">
                              {(item.solutions || []).map((sol, i) => (
                                <li
                                  key={i}
                                  className="home-subnote group/item flex items-start gap-4 text-gray-300 transition-colors hover:text-white"
                                >
                                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-500/50 transition-colors group-hover/item:bg-green-400" />
                                  {sol}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="relative z-20 flex shrink-0 justify-end pt-6">
                        <div className="text-right">
                          <div className="home-card-title mb-2 bg-gradient-to-br from-white to-gray-500 bg-clip-text leading-none font-bold text-transparent">
                            {item.stats}
                          </div>
                          <div className="home-meta font-bold text-gray-500 uppercase">
                            {item.statsLabel}
                          </div>
                        </div>
                      </div>
                    </m.div>
                  ) : (
                    <m.div
                      key="collapsed"
                      layout="position"
                      initial={{ opacity: 0, scale: 0.99 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.99 }}
                      transition={
                        reduceMotion
                          ? { duration: 0 }
                          : { duration: 0.28, ease: [0.22, 1, 0.36, 1] }
                      }
                      className="flex min-h-[140px] flex-1 flex-col items-center justify-center md:min-h-[240px] lg:min-h-0"
                    >
                      <div className="text-center lg:hidden">
                        <h3 className="home-card-title-premium-sm text-gray-200 uppercase">
                          {item.title}
                        </h3>
                        <span
                          className={`home-meta mt-3 inline-block bg-gradient-to-r font-bold tracking-[0.3em] uppercase ${item.color} bg-clip-text text-transparent opacity-90`}
                        >
                          {item.subtitle}
                        </span>
                        {item.description ? (
                          <p className="home-subnote mt-6 max-w-[28rem] leading-relaxed text-gray-500">
                            {item.description}
                          </p>
                        ) : null}
                      </div>

                      <div className="pointer-events-none relative hidden h-full w-full items-center justify-center lg:flex">
                        <span className="-rotate-90 text-[clamp(1.15rem,1.5vw,1.55rem)] font-semibold tracking-[0.24em] whitespace-nowrap text-white/58 drop-shadow-[0_0_12px_rgba(255,255,255,0.14)] transition-all duration-300">
                          {String(item.title || '').toUpperCase()}
                        </span>
                      </div>
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            </m.div>
          )
        })}
      </div>
    </section>
  )
}
